/*
  Leep theme scripts. No build step, no dependencies (Lenis is optional for smooth scrolling).
  Every component initialises on DOMContentLoaded and again when the theme editor reloads a section.
*/
(function () {
  'use strict';

  var Leep = window.Leep || {};
  var settings = Leep.settings || {};
  var routes = Leep.routes || {};
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  var lenis = null;

  /* ---------- helpers ---------- */

  function $(selector, root) { return (root || document).querySelector(selector); }
  function $$(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function rootUrl(path) {
    var root = (routes.root || '/').replace(/\/$/, '');
    return root + path;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || 'Request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function scrollToTarget(target, offset) {
    offset = offset || 0;
    if (lenis) { lenis.scrollTo(target, { offset: -offset }); return; }
    var top = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  function lockScroll(lock, className) {
    document.documentElement.classList.toggle(className, lock);
    if (lenis) { if (lock) lenis.stop(); else lenis.start(); }
  }

  /* ---------- smooth scrolling ---------- */

  function initSmoothScroll() {
    if (!settings.smoothScroll || prefersReducedMotion || isTouch || typeof window.Lenis === 'undefined') return;
    try {
      lenis = new window.Lenis({ lerp: 0.1, smooth: true });
      var raf = function (time) { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    } catch (e) { lenis = null; }
  }

  /* ---------- navigation ---------- */

  function initNav() {
    var navbar = $('[data-header]');
    if (!navbar || navbar.dataset.ready) return;
    navbar.dataset.ready = '1';
    var toggle = $('[data-nav-toggle]', navbar);
    if (!toggle) return;

    function setOpen(open) {
      navbar.classList.toggle('is-open', open);
      toggle.classList.toggle('w--open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      lockScroll(open, 'nav-open');
    }
    toggle.addEventListener('click', function () { setOpen(!navbar.classList.contains('is-open')); });
    $$('.nav-link', navbar).forEach(function (link) {
      link.addEventListener('click', function () { if (window.innerWidth < 992) setOpen(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && navbar.classList.contains('is-open')) { setOpen(false); toggle.focus(); }
    });
    window.addEventListener('resize', function () { if (window.innerWidth >= 992 && navbar.classList.contains('is-open')) setOpen(false); });
  }

  /* ---------- in-page anchor links ---------- */

  function headerOffset() {
    var navbar = $('[data-header]');
    var links = $('[data-section-links]');
    var offset = 0;
    if (links) offset += links.offsetHeight;
    else if (navbar) offset += navbar.offsetHeight + 16;
    return offset;
  }

  function initAnchorLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href^="#"], a[href^="/#"]');
      if (!link) return;
      var href = link.getAttribute('href');
      var hash = href.slice(href.indexOf('#'));
      if (href.charAt(0) === '/' && window.location.pathname !== '/') return;
      if (hash === '#' || hash.length < 2) return;
      var target;
      try { target = document.querySelector(hash); } catch (err) { return; }
      if (!target) return;
      e.preventDefault();
      scrollToTarget(target, headerOffset());
      if (target.hasAttribute('tabindex') || target.matches('a[href], button, input, select, textarea')) {
        target.focus({ preventScroll: true });
      }
      if (history.replaceState) history.replaceState(null, '', hash);
    });
    $$('[data-scroll-top]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); scrollToTarget(0); });
    });
  }

  /* ---------- sticky section links ---------- */

  function initSectionLinks(root) {
    $$('[data-section-links]', root).forEach(function (nav) {
      if (nav.dataset.ready) return;
      nav.dataset.ready = '1';
      var links = $$('[data-section-link]', nav);
      var targets = links.map(function (l) { return document.getElementById(l.dataset.sectionLink); });
      if (!('IntersectionObserver' in window)) return;
      var visible = {};
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { visible[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0; });
        var best = null; var bestRatio = 0;
        Object.keys(visible).forEach(function (id) { if (visible[id] > bestRatio) { bestRatio = visible[id]; best = id; } });
        links.forEach(function (l) { l.classList.toggle('w--current', l.dataset.sectionLink === best); });
      }, { threshold: [0, 0.15, 0.3, 0.5], rootMargin: '-20% 0px -40% 0px' });
      targets.forEach(function (t) { if (t) observer.observe(t); });
    });
  }

  /* ---------- reveal on scroll ---------- */

  var REVEAL_SELECTORS = [
    '.blog-header-text', '.blog-list-header', '.blog-item',
    '.blog-article-author', '.article-intro', '.article-text', '.blog-article-author-bio',
    '.support-tab', '.press-item', '.leep-compare-card', '.sizing-steps__row', '._3-col-grid > div'
  ];

  function initReveal(root) {
    if (!settings.reveal || prefersReducedMotion || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('reveal-enabled');
    var els = $$(REVEAL_SELECTORS.join(','), root).filter(function (el) { return !el.hasAttribute('data-reveal'); });
    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); obs.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) {
      el.setAttribute('data-reveal', '');
      // Anything already on screen shows straight away.
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
      else observer.observe(el);
    });
  }

  /* ---------- Webflow-style sliders ---------- */

  // Slides after the first get their photo only when the slider is close to the screen.
  function loadSlideImages(slider) {
    $$('[data-bg-style]', slider).forEach(function (slide) {
      slide.setAttribute('style', (slide.getAttribute('style') || '') + slide.getAttribute('data-bg-style'));
      slide.removeAttribute('data-bg-style');
    });
  }

  function initSliders(root) {
    $$('[data-slider]', root).forEach(function (slider) {
      if (!slider.dataset.lazyWatched) {
        slider.dataset.lazyWatched = '1';
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            if (entries.some(function (e) { return e.isIntersecting; })) { loadSlideImages(slider); io.disconnect(); }
          }, { rootMargin: '600px 0px' });
          io.observe(slider);
        } else {
          loadSlideImages(slider);
        }
      }
      if (slider.dataset.ready) return;
      var mask = $('[data-slider-mask]', slider);
      var slides = $$('[data-slide]', slider);
      if (!mask || slides.length < 2) return;
      slider.dataset.ready = '1';
      var count = slides.length;
      var current = 0;
      var dotsWrap = $('[data-slider-dots]', slider);
      var dots = [];
      var timer = null;

      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        slides.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'w-slider-dot';
          dot.setAttribute('aria-label', 'Show slide ' + (i + 1) + ' of ' + count);
          dot.addEventListener('click', function () { go(i); });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      }

      function offsetFor(i, index) {
        var d = i - index;
        if (d > count / 2) d -= count;
        if (d < -count / 2) d += count;
        return d;
      }

      function layout(index, dragPx, animate) {
        slides.forEach(function (slide, i) {
          var prev = slide._offset;
          var next = offsetFor(i, index);
          // Slides that wrap around to the other side should jump, not fly across.
          var jump = prev !== undefined && Math.abs(next - prev) > 1;
          slide.classList.toggle('no-transition', !animate || jump);
          slide.style.transform = 'translateX(calc(' + ((next - i) * 100) + '% + ' + (dragPx || 0) + 'px))';
          slide._offset = next;
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          if (i === index) slide.removeAttribute('inert'); else slide.setAttribute('inert', '');
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('w-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      function go(index) {
        current = (index + count) % count;
        layout(current, 0, true);
        restart();
      }

      function restart() {
        if (slider.dataset.autoplay !== 'true') return;
        clearInterval(timer);
        timer = setInterval(function () { go(current + 1); }, parseInt(slider.dataset.delay, 10) || 4000);
      }

      var prev = $('[data-slider-prev]', slider);
      var next = $('[data-slider-next]', slider);
      if (prev) prev.addEventListener('click', function () { go(current - 1); });
      if (next) next.addEventListener('click', function () { go(current + 1); });
      slider.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') go(current - 1);
        if (e.key === 'ArrowRight') go(current + 1);
      });

      // Swipe / drag
      var startX = null; var startY = null; var dx = 0; var dragging = false;
      mask.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX; startY = e.clientY; dx = 0; dragging = false;
      });
      mask.addEventListener('pointermove', function (e) {
        if (startX === null) return;
        var moveX = e.clientX - startX;
        var moveY = e.clientY - startY;
        if (!dragging && Math.abs(moveX) > 8 && Math.abs(moveX) > Math.abs(moveY)) {
          dragging = true;
          mask.classList.add('is-dragging');
          try { mask.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        }
        if (dragging) { dx = moveX; layout(current, dx, false); }
      });
      function endDrag() {
        if (startX === null) return;
        mask.classList.remove('is-dragging');
        if (dragging) {
          var threshold = Math.min(80, mask.offsetWidth * 0.15);
          if (dx < -threshold) go(current + 1);
          else if (dx > threshold) go(current - 1);
          else layout(current, 0, true);
        }
        startX = null; dragging = false;
      }
      mask.addEventListener('pointerup', endDrag);
      mask.addEventListener('pointercancel', endDrag);
      mask.addEventListener('click', function (e) { if (Math.abs(dx) > 8) { e.preventDefault(); e.stopPropagation(); dx = 0; } }, true);

      layout(0, 0, false);
      restart();
    });
  }

  /* ---------- accordions + keyword search ---------- */

  function setAccordion(item, open) {
    var content = $('[data-accordion-content]', item);
    var toggle = $('[data-accordion-toggle]', item);
    if (!content || !toggle) return;
    var isOpen = item.classList.contains('is-open');
    if (open === isOpen) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (prefersReducedMotion) { item.classList.toggle('is-open', open); return; }
    if (open) {
      item.classList.add('is-open');
      content.style.height = '0px';
      var target = content.scrollHeight;
      requestAnimationFrame(function () { content.style.height = target + 'px'; });
    } else {
      content.style.height = content.scrollHeight + 'px';
      content.offsetHeight; // force reflow
      item.classList.remove('is-open');
      requestAnimationFrame(function () { content.style.height = '0px'; });
    }
    content.addEventListener('transitionend', function handler(e) {
      if (e.propertyName !== 'height') return;
      content.style.height = '';
      content.removeEventListener('transitionend', handler);
    });
  }

  /* ---------- lazy video: only download the ring animation when it is about to come into view ---------- */

  function initLazyVideos(root) {
    $$('video[data-lazy-video]', root).forEach(function (video) {
      if (video.dataset.lazyWatched) return;
      video.dataset.lazyWatched = '1';
      var start = function () {
        $$('source[data-src]', video).forEach(function (source) {
          source.src = source.getAttribute('data-src');
          source.removeAttribute('data-src');
        });
        video.load();
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      };
      if (!('IntersectionObserver' in window)) { start(); return; }
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); start(); }
      }, { rootMargin: '400px 0px' });
      io.observe(video);
    });
  }

  /* ---------- comparison pages: "Only show differences" ---------- */

  function initCompareToggle(root) {
    $$('[data-diff-toggle]', root).forEach(function (toggle) {
      if (toggle.dataset.ready) return;
      toggle.dataset.ready = '1';
      var table = toggle.closest('.compare-content').querySelector('[data-compare-table]');
      if (!table) return;
      toggle.addEventListener('change', function () {
        $$('[data-compare-row]', table).forEach(function (row) {
          var values = $$('[data-val]', row).map(function (cell) { return cell.textContent.trim().toLowerCase(); });
          row.hidden = toggle.checked && values.length === 2 && values[0] === values[1];
        });
      });
    });
  }

  /* ---------- product reviews: only take up space once the reviews app has drawn something ---------- */

  function initReviews(root) {
    $$('[data-product-reviews]', root).forEach(function (section) {
      var inner = section.querySelector('.container-large') || section;
      var update = function () { section.classList.toggle('has-content', inner.offsetHeight > 8); };
      update();
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(inner);
    });
  }

  function initAccordions(root) {
    $$('[data-accordion-item]', root).forEach(function (item) {
      if (item.dataset.ready) return;
      item.dataset.ready = '1';
      var toggle = $('[data-accordion-toggle]', item);
      if (toggle) toggle.addEventListener('click', function () { setAccordion(item, !item.classList.contains('is-open')); });
    });

    $$('[data-accordion-search]', root).forEach(function (bar) {
      if (bar.dataset.ready) return;
      bar.dataset.ready = '1';
      var scope = bar.closest('.support-content') || bar.parentElement;
      var list = $('[data-accordion-list]', scope);
      var input = $('[data-search-input]', bar);
      var clear = $('[data-search-clear]', bar);
      var countEl = $('[data-search-count]', bar);
      var empty = $('[data-search-empty]', scope);
      if (!list || !input) return;
      var items = $$('[data-accordion-item]', list).map(function (item) {
        var q = $('[data-accordion-question]', item);
        var a = $('[data-accordion-answer]', item);
        return { el: item, q: q, original: q ? q.textContent : '', text: ((q ? q.textContent : '') + ' ' + (a ? a.textContent : '')).toLowerCase() };
      });
      function escapeHtml(s) { return s.replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
      function render() {
        var term = input.value.trim().toLowerCase();
        var words = term.split(/\s+/).filter(Boolean);
        var shown = 0;
        items.forEach(function (it) {
          var match = words.every(function (w) { return it.text.indexOf(w) !== -1; });
          it.el.classList.toggle('ls-hide', !match);
          if (match) shown++;
          if (it.q) {
            if (!words.length) { it.q.textContent = it.original; return; }
            var html = escapeHtml(it.original);
            words.forEach(function (w) {
              var re = new RegExp('(' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
              html = html.replace(re, '<mark class="ls-mark">$1</mark>');
            });
            it.q.innerHTML = html;
          }
        });
        if (clear) clear.classList.toggle('show', term.length > 0);
        if (countEl) countEl.innerHTML = words.length ? '<strong>' + shown + '</strong> of ' + items.length + ' topics' : items.length + ' topics';
        if (empty) empty.classList.toggle('show', shown === 0);
      }
      input.addEventListener('input', render);
      if (clear) clear.addEventListener('click', function () { input.value = ''; render(); input.focus(); });
      render();
    });
  }

  /* ---------- cart ---------- */

  var Cart = {
    drawer: null,
    lastFocus: null,

    state: function () {
      var el = $('[data-cart-json]');
      if (!el) return { items: [], item_count: 0, attributes: {} };
      try { return JSON.parse(el.textContent); } catch (e) { return { items: [], item_count: 0, attributes: {} }; }
    },

    kitHandle: function () { return (this.drawer && this.drawer.dataset.kitHandle) || 'leep-sizing-kit'; },
    ringHandle: function () { return (this.drawer && this.drawer.dataset.ringHandle) || 'leep-ring'; },

    init: function () {
      this.drawer = $('[data-cart-drawer]');
      var self = this;
      document.addEventListener('click', function (e) {
        var toggle = e.target.closest && e.target.closest('[data-cart-toggle]');
        if (toggle) { e.preventDefault(); self.open(toggle); }
      });
      if (!this.drawer) return;
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-close]')) { self.close(); return; }
        var line = e.target.closest('[data-line-key]');
        if (!line) return;
        var qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          var qty = Math.max(0, parseInt(line.dataset.quantity, 10) + parseInt(qtyBtn.dataset.qtyChange, 10));
          self.change(line.dataset.lineKey, qty);
        }
        if (e.target.closest('[data-remove]')) self.change(line.dataset.lineKey, 0);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer.classList.contains('is-open')) self.close();
        if (e.key === 'Tab' && self.drawer.classList.contains('is-open')) self.trapFocus(e);
      });
      this.enforceKitRules(this.state());
    },

    open: function (opener) {
      if (!this.drawer) { window.location.href = routes.cart || '/cart'; return; }
      this.lastFocus = opener || document.activeElement;
      this.drawer.classList.add('is-open');
      lockScroll(true, 'cart-open');
      var content = $('.cart-drawer-content', this.drawer);
      if (content) setTimeout(function () { content.focus(); }, 50);
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      lockScroll(false, 'cart-open');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    trapFocus: function (e) {
      var focusable = $$('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])', this.drawer)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;
      var first = focusable[0]; var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    busy: function (on) {
      var body = this.drawer && $('[data-cart-body]', this.drawer);
      if (body) body.classList.toggle('is-busy', on);
    },

    renderSections: function (sections) {
      if (!sections || !sections['cart-drawer'] || !this.drawer) return;
      var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var newBody = $('[data-cart-body]', doc);
      var newJson = $('[data-cart-json]', doc);
      var body = $('[data-cart-body]', this.drawer);
      if (newBody && body) body.innerHTML = newBody.innerHTML;
      var json = $('[data-cart-json]');
      if (newJson && json) json.textContent = newJson.textContent;
      var error = $('[data-cart-error]', this.drawer);
      if (error) error.hidden = true;
      this.updateCount();
    },

    updateCount: function () {
      var count = this.state().item_count || 0;
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    refresh: function () {
      var self = this;
      return fetch(rootUrl('/?sections=cart-drawer'))
        .then(function (r) { return r.json(); })
        .then(function (sections) { self.renderSections(sections); return self.state(); });
    },

    add: function (items) {
      var self = this;
      this.busy(true);
      return postJSON(rootUrl('/cart/add.js'), {
        items: items,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      }).then(function (res) {
        self.renderSections(res.sections);
        return self.enforceKitRules(self.state());
      }).finally(function () { self.busy(false); });
    },

    change: function (key, quantity) {
      var self = this;
      this.busy(true);
      return postJSON(rootUrl('/cart/change.js'), {
        id: key,
        quantity: quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      }).then(function (res) {
        self.renderSections(res.sections);
        return self.enforceKitRules(self.state());
      }).catch(function (err) {
        var message = (err.data && (err.data.description || err.data.message)) || "Sorry, that change couldn't be saved. Please try again.";
        return self.refresh().catch(function () {}).then(function () { self.showError(message); });
      }).finally(function () { self.busy(false); });
    },

    showError: function (message) {
      var el = this.drawer && $('[data-cart-error]', this.drawer);
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    },

    // The free sizing kit only belongs in the cart alongside a ring ordered as "Size Unknown", and only once.
    enforceKitRules: function (cart) {
      var kitHandle = this.kitHandle();
      var ringHandle = this.ringHandle();
      var sizeUnknown = (this.drawer && this.drawer.dataset.sizeUnknown) || 'Size Unknown';
      var needsKit = cart.items.some(function (it) {
        return it.handle === ringHandle && (it.variant_title || '').indexOf(sizeUnknown) !== -1;
      });
      var updates = {};
      var needed = false;
      cart.items.forEach(function (it) {
        if (it.handle !== kitHandle) return;
        if (!needsKit) { updates[it.key] = 0; needed = true; }
        else if (it.quantity > 1) { updates[it.key] = 1; needed = true; }
      });
      if (!needed) return Promise.resolve(cart);
      var self = this;
      return postJSON(rootUrl('/cart/update.js'), {
        updates: updates,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      }).then(function (res) {
        self.renderSections(res.sections);
        // The full cart page is server-rendered; reload it so it matches.
        if (document.body.classList.contains('template-cart')) window.location.reload();
        return self.state();
      }).catch(function () {
        // A failed tidy-up must not look like a failed add; the next cart change will retry it.
        return self.state();
      });
    }
  };

  /* ---------- product page ---------- */

  function initProduct(root) {
    $$('[data-product-section]', root).forEach(function (section) {
      if (section.dataset.ready) return;
      var jsonEl = $('[data-product-json]', section);
      if (!jsonEl) return;
      section.dataset.ready = '1';
      var product = JSON.parse(jsonEl.textContent);
      var form = $('[data-product-form]', section);
      var variantInput = $('[data-variant-input]', section);
      var button = $('[data-add-to-cart]', section);
      var buttonLabel = $('[data-add-label]', section);
      var priceEl = $('[data-price-amount]', section);
      var errorEl = $('[data-form-error]', section);
      var colourGroup = product.colourIndex >= 0 ? $('[data-option-index="' + product.colourIndex + '"]', section) : null;
      var sizeGroup = product.sizeIndex >= 0 ? $('[data-option-index="' + product.sizeIndex + '"]', section) : null;
      var dropdown = $('[data-size-dropdown]', section);
      var dropdownLabel = $('[data-size-toggle-label]', section);

      var initial = product.variants.find(function (v) { return String(v.id) === String(variantInput.value); }) || product.variants[0];
      var selected = initial.options.slice();

      function currentVariant() {
        return product.variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === selected[i]; });
        });
      }

      function kitChosen() { return product.sizeIndex >= 0 && selected[product.sizeIndex] === product.sizeUnknown; }

      function updateGalleries() {
        if (product.colourIndex < 0) return;
        var colour = selected[product.colourIndex];
        $$('[data-gallery]', section).forEach(function (g) {
          var active = g.dataset.colour === colour;
          g.classList.toggle('is-active', active);
          if (active) setGallerySlide(g, 0, true);
        });
      }

      function updateOptionsUI() {
        if (colourGroup) {
          $$('[data-value]', colourGroup).forEach(function (btn) {
            var on = btn.dataset.value === selected[product.colourIndex];
            btn.classList.toggle('is-active', on);
            btn.setAttribute('aria-checked', on ? 'true' : 'false');
          });
        }
        if (sizeGroup) {
          var size = selected[product.sizeIndex];
          var kitBtn = $('.sizing-kit-button', sizeGroup);
          if (kitBtn) {
            kitBtn.classList.toggle('is-active', size === product.sizeUnknown);
            kitBtn.setAttribute('aria-pressed', size === product.sizeUnknown ? 'true' : 'false');
          }
          $$('.size-button', sizeGroup).forEach(function (btn) {
            var on = btn.dataset.value === size;
            btn.classList.toggle('active', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
            // Grey out sizes that are sold out in the chosen colour.
            var probe = selected.slice(); probe[product.sizeIndex] = btn.dataset.value;
            var v = product.variants.find(function (x) { return x.options.every(function (o, i) { return o === probe[i]; }); });
            var soldOut = !v || !v.available;
            btn.classList.toggle('is-unavailable', soldOut);
            btn.setAttribute('aria-label', product.labels.size + ' ' + btn.dataset.value + (soldOut ? ' – ' + product.labels.soldOut : ''));
          });
          if (dropdown) dropdown.classList.toggle('has-size', size !== product.sizeUnknown);
          if (dropdownLabel) {
            dropdownLabel.textContent = size && size !== product.sizeUnknown
              ? product.labels.size + ' ' + size
              : dropdownLabel.dataset.defaultLabel;
          }
        }
      }

      function update(pushUrl) {
        var variant = currentVariant();
        updateOptionsUI();
        if (errorEl) errorEl.hidden = true;
        if (!variant) {
          button.disabled = true;
          buttonLabel.textContent = product.labels.select;
          return;
        }
        variantInput.value = variant.id;
        if (priceEl) priceEl.innerHTML = variant.priceFormatted;
        var compare = $('[data-compare-price]', section);
        if (compare) compare.hidden = !(variant.compareAtPrice > variant.price);
        if (variant.available) {
          button.disabled = false;
          buttonLabel.textContent = kitChosen() && product.kitVariantId ? product.labels.addKit : product.labels.add;
        } else {
          button.disabled = true;
          buttonLabel.textContent = product.labels.soldOut;
        }
        if (pushUrl && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      if (colourGroup) {
        colourGroup.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-value]');
          if (!btn) return;
          selected[product.colourIndex] = btn.dataset.value;
          updateGalleries();
          update(true);
        });
      }

      if (sizeGroup) {
        sizeGroup.addEventListener('click', function (e) {
          if (e.target.closest('[data-size-toggle]')) {
            var open = !dropdown.classList.contains('is-open');
            dropdown.classList.toggle('is-open', open);
            $('[data-size-toggle]', dropdown).setAttribute('aria-expanded', open ? 'true' : 'false');
            return;
          }
          var btn = e.target.closest('[data-value]');
          if (!btn) return;
          selected[product.sizeIndex] = btn.dataset.value;
          if (btn.classList.contains('sizing-kit-button') && dropdown) {
            dropdown.classList.remove('is-open');
            $('[data-size-toggle]', dropdown).setAttribute('aria-expanded', 'false');
          }
          update(true);
        });
        document.addEventListener('click', function (e) {
          if (dropdown && dropdown.classList.contains('is-open') && !sizeGroup.contains(e.target)) {
            dropdown.classList.remove('is-open');
            $('[data-size-toggle]', dropdown).setAttribute('aria-expanded', 'false');
          }
        });
      }

      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var variant = currentVariant();
          if (!variant || !variant.available) return;
          var items = [{ id: variant.id, quantity: 1 }];
          if (kitChosen() && product.kitVariantId) {
            var kitInCart = Cart.state().items.some(function (it) { return String(it.variant_id) === String(product.kitVariantId); });
            if (!kitInCart) items.push({ id: product.kitVariantId, quantity: 1 });
          }
          button.disabled = true;
          button.classList.add('is-loading');
          Cart.add(items).then(function () {
            if (settings.openDrawerOnAdd !== false) Cart.open(button);
          }).catch(function (err) {
            if (errorEl) {
              errorEl.textContent = (err.data && (err.data.description || err.data.message)) || 'Sorry, something went wrong. Please try again.';
              errorEl.hidden = false;
            }
          }).finally(function () {
            button.disabled = false;
            button.classList.remove('is-loading');
            update(false);
          });
        });
      }

      initGalleries(section);
      initLightbox(section);
      update(false);
    });
  }

  function setGallerySlide(gallery, index, instant) {
    var viewport = $('[data-gallery-viewport]', gallery);
    if (!viewport) return;
    var slides = $$('[data-gallery-slide]', gallery);
    if (!slides.length) return;
    index = (index + slides.length) % slides.length;
    viewport.scrollTo({ left: index * viewport.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    markThumb(gallery, index);
  }

  function markThumb(gallery, index) {
    $$('[data-thumb]', gallery).forEach(function (t, i) {
      t.classList.toggle('is-active', i === index);
      if (i === index && t.parentElement.scrollWidth > t.parentElement.clientWidth) {
        t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
      }
    });
  }

  function initGalleries(root) {
    $$('[data-gallery]', root).forEach(function (gallery) {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = '1';
      var viewport = $('[data-gallery-viewport]', gallery);
      if (!viewport) return;
      function currentIndex() { return Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth)); }
      var prev = $('[data-gallery-prev]', gallery);
      var next = $('[data-gallery-next]', gallery);
      if (prev) prev.addEventListener('click', function () { setGallerySlide(gallery, currentIndex() - 1); });
      if (next) next.addEventListener('click', function () { setGallerySlide(gallery, currentIndex() + 1); });
      $$('[data-thumb]', gallery).forEach(function (thumb) {
        thumb.addEventListener('click', function () { setGallerySlide(gallery, parseInt(thumb.dataset.thumb, 10)); });
      });
      var ticking = false;
      viewport.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { markThumb(gallery, currentIndex()); ticking = false; });
      }, { passive: true });
      viewport.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); setGallerySlide(gallery, currentIndex() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); setGallerySlide(gallery, currentIndex() + 1); }
      });
    });
  }

  function initLightbox(section) {
    var dialog = $('[data-lightbox]', section);
    if (!dialog || typeof dialog.showModal !== 'function') return;
    var img = $('[data-lightbox-image]', dialog);
    section.addEventListener('click', function (e) {
      var slideImg = e.target.closest('[data-gallery-slide] img');
      if (!slideImg) return;
      img.src = slideImg.currentSrc || slideImg.src;
      img.alt = slideImg.alt || '';
      dialog.showModal();
      lockScroll(true, 'cart-open');
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog || e.target.closest('[data-lightbox-close]')) dialog.close();
    });
    dialog.addEventListener('close', function () { lockScroll(false, 'cart-open'); });
  }

  /* ---------- localisation ---------- */

  function initLocalization(root) {
    $$('select[data-autosubmit]', root).forEach(function (select) {
      if (select.dataset.ready) return;
      select.dataset.ready = '1';
      select.addEventListener('change', function () { select.form.submit(); });
    });
  }

  /* ---------- boot ---------- */

  function initAll(root) {
    initSliders(root);
    initLazyVideos(root);
    initCompareToggle(root);
    initReviews(root);
    initAccordions(root);
    initProduct(root);
    initGalleries(root);
    initSectionLinks(root);
    initLocalization(root);
    initReveal(root);
  }

  onReady(function () {
    initSmoothScroll();
    initNav();
    initAnchorLinks();
    Cart.init();
    initAll(document);
    // Arriving from another page with #section in the URL: account for the fixed header.
    if (window.location.hash && window.location.hash.length > 1) {
      var target;
      try { target = document.querySelector(window.location.hash); } catch (e) { target = null; }
      if (target) setTimeout(function () { scrollToTarget(target, headerOffset()); }, 300);
    }
  });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); initNav(); });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest('[data-slide]');
    if (slide) {
      var slider = slide.closest('[data-slider]');
      loadSlideImages(slider);
      var index = $$('[data-slide]', slider).indexOf(slide);
      var dot = $$('.w-slider-dot', slider)[index];
      if (dot) dot.click();
    }
  });

  window.LeepCart = Cart;
})();
